<template>
  <div class="ticket-center-page" :class="{ embedded }">
    <div class="container">
      <section v-if="!embedded" class="ticket-hero">
        <div>
          <span class="section-kicker">{{ $t("ticketCenter.kicker") }}</span>
          <h1>{{ $t("ticketCenter.title") }}</h1>
          <p>{{ $t("ticketCenter.subtitle") }}</p>
        </div>
        <el-button type="primary" size="large" @click="openCreateDialog">
          <span class="button-symbol" aria-hidden="true">＋</span>
          {{ $t("ticketCenter.create") }}
        </el-button>
      </section>

      <div v-else class="embedded-actions">
        <el-button type="primary" size="large" @click="openCreateDialog">
          <span class="button-symbol" aria-hidden="true">＋</span>
          {{ $t("ticketCenter.create") }}
        </el-button>
      </div>

      <section class="ticket-shell">
        <aside class="ticket-list-card">
          <div class="list-toolbar">
            <div
              class="status-filter"
              role="tablist"
              :aria-label="$t('ticketCenter.statusFilter')"
            >
              <button
                v-for="option in statusOptions"
                :key="String(option.value)"
                type="button"
                class="filter-chip"
                :class="{ active: statusFilter === option.value }"
                @click="changeStatusFilter(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
            <button
              type="button"
              class="refresh-button"
              :aria-label="$t('common.refresh')"
              :disabled="listLoading"
              @click="fetchTickets"
            >
              ↻
            </button>
          </div>

          <div v-if="listLoading" class="list-loading">
            <el-skeleton v-for="index in 4" :key="index" :rows="2" animated />
          </div>

          <div v-else-if="tickets.length" class="ticket-list">
            <button
              v-for="ticket in tickets"
              :key="ticket.id"
              type="button"
              class="ticket-list-item"
              :class="{ selected: selectedTicketId === ticket.id }"
              @click="openTicket(ticket)"
            >
              <div class="ticket-item-topline">
                <span class="ticket-number">{{ ticket.ticketNo }}</span>
                <span
                  v-if="ticket.userUnread"
                  class="unread-dot"
                  :title="$t('ticketCenter.unread')"
                ></span>
                <span class="status-pill" :class="statusClass(ticket.status)">
                  {{ statusLabel(ticket.status) }}
                </span>
              </div>
              <div class="ticket-item-type">{{ typeLabel(ticket.type) }}</div>
              <p class="ticket-summary">{{ ticket.summary }}</p>
              <time>{{
                formatTime(
                  ticket.lastMessageTime ||
                    ticket.updateTime ||
                    ticket.createTime
                )
              }}</time>
            </button>
          </div>

          <div v-else class="empty-list">
            <div class="empty-icon" aria-hidden="true">◎</div>
            <h3>{{ $t("ticketCenter.emptyTitle") }}</h3>
            <p>{{ $t("ticketCenter.emptyDescription") }}</p>
            <el-button type="primary" plain @click="openCreateDialog">
              {{ $t("ticketCenter.createFirst") }}
            </el-button>
          </div>

          <el-pagination
            v-if="total > pageSize"
            v-model:current-page="currentPage"
            :page-size="pageSize"
            :total="total"
            layout="prev, pager, next"
            small
            @current-change="fetchTickets"
          />
        </aside>

        <main class="ticket-detail-card">
          <div v-if="detailLoading" class="detail-loading">
            <el-skeleton :rows="8" animated />
          </div>

          <div v-else-if="detailTicket" class="detail-content">
            <header class="detail-header">
              <div>
                <div class="detail-eyebrow">
                  <span>{{ detailTicket.ticketNo }}</span>
                  <span
                    class="status-pill"
                    :class="statusClass(detailTicket.status)"
                  >
                    {{ statusLabel(detailTicket.status) }}
                  </span>
                </div>
                <h2>{{ typeLabel(detailTicket.type) }}</h2>
                <div class="detail-meta">
                  <span
                    >{{ $t("ticketCenter.createdAt") }}
                    {{ formatTime(detailTicket.createTime) }}</span
                  >
                  <span v-if="detailTicket.relatedId">
                    {{ $t("ticketCenter.relatedRecord") }}
                    {{ detailTicket.relatedId }}
                  </span>
                </div>
              </div>
              <div class="detail-actions">
                <el-button
                  v-if="canCancel"
                  text
                  type="danger"
                  :loading="actionLoading"
                  @click="handleCancel"
                >
                  {{ $t("ticketCenter.cancelTicket") }}
                </el-button>
                <el-button
                  v-if="canClose"
                  type="success"
                  plain
                  :loading="actionLoading"
                  @click="handleClose"
                >
                  {{ $t("ticketCenter.confirmClose") }}
                </el-button>
              </div>
            </header>

            <div
              v-if="detailTicket.status === TicketStatus.WAIT_USER"
              class="attention-banner"
            >
              <span aria-hidden="true">!</span>
              {{ $t("ticketCenter.waitUserHint") }}
            </div>
            <div
              v-else-if="detailTicket.status === TicketStatus.RESOLVED"
              class="resolved-banner"
            >
              <span aria-hidden="true">✓</span>
              {{ $t("ticketCenter.resolvedHint") }}
            </div>

            <section
              ref="timelineRef"
              class="timeline"
              :aria-label="$t('ticketCenter.timeline')"
            >
              <div v-if="messageHasMore" class="timeline-load-older">
                <el-button
                  text
                  :loading="olderMessagesLoading"
                  @click="loadOlderMessages"
                >
                  {{ $t("ticketCenter.loadOlderMessages") }}
                </el-button>
              </div>
              <article
                v-for="message in messages"
                :key="message.id"
                class="timeline-item"
                :class="senderClass(message)"
              >
                <div class="timeline-avatar" aria-hidden="true">
                  {{ senderInitial(message) }}
                </div>
                <div class="timeline-body">
                  <div class="timeline-meta">
                    <strong>{{ senderLabel(message) }}</strong>
                    <time>{{ formatTime(message.createTime) }}</time>
                  </div>
                  <p>{{ message.content }}</p>
                  <el-image
                    v-for="image in message.attachments || []"
                    :key="image.id"
                    class="ticket-image"
                    :src="image.url"
                    :preview-src-list="
                      (message.attachments || []).map((item) => item.url)
                    "
                    fit="cover"
                    lazy
                  />
                </div>
              </article>
            </section>

            <section v-if="canReply" class="reply-panel">
              <label for="ticket-reply">{{
                $t("ticketCenter.addReply")
              }}</label>
              <el-input
                id="ticket-reply"
                v-model="replyContent"
                type="textarea"
                :rows="4"
                maxlength="2000"
                show-word-limit
                resize="vertical"
                :placeholder="$t('ticketCenter.replyPlaceholder')"
              />
              <div class="attachment-picker">
                <input
                  ref="replyFileInput"
                  hidden
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  @change="selectReplyImages"
                />
                <el-button
                  :disabled="replyUploading || replyAttachments.length >= 3"
                  @click="replyFileInput?.click()"
                  >选择图片</el-button
                >
                <span>JPEG/PNG，单张不超过 5MB，最多 3 张</span>
              </div>
              <div v-if="replyAttachments.length" class="attachment-list">
                <div
                  v-for="item in replyAttachments"
                  :key="item.localId"
                  class="attachment-item"
                >
                  <img :src="item.preview" :alt="item.name" />
                  <span>{{
                    item.status === "uploading"
                      ? "上传中"
                      : item.status === "done"
                      ? "已上传"
                      : "上传失败"
                  }}</span>
                  <button
                    type="button"
                    @click.stop="removeReplyAttachment(item)"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div class="reply-actions">
                <span>{{ $t("ticketCenter.replyPrivacyHint") }}</span>
                <el-button
                  type="primary"
                  :disabled="
                    replyUploading ||
                    (!replyContent.trim() &&
                      !completedIds(replyAttachments).length)
                  "
                  :loading="replyLoading"
                  @click="handleReply"
                >
                  {{
                    detailTicket.status === TicketStatus.RESOLVED
                      ? $t("ticketCenter.reopenAndReply")
                      : $t("ticketCenter.sendReply")
                  }}
                </el-button>
              </div>
            </section>

            <section v-else class="terminal-panel">
              <strong>{{ $t("ticketCenter.ticketEnded") }}</strong>
              <p>{{ $t("ticketCenter.ticketEndedHint") }}</p>
              <el-button type="primary" plain @click="openCreateDialog">
                {{ $t("ticketCenter.createAnother") }}
              </el-button>
            </section>
          </div>

          <div v-else class="detail-placeholder">
            <div class="placeholder-illustration" aria-hidden="true">
              <span></span><span></span><span></span>
            </div>
            <h2>{{ $t("ticketCenter.selectTitle") }}</h2>
            <p>{{ $t("ticketCenter.selectDescription") }}</p>
          </div>
        </main>
      </section>
    </div>

    <el-dialog
      v-model="createDialogVisible"
      :title="$t('ticketCenter.createTitle')"
      width="min(620px, calc(100vw - 32px))"
      destroy-on-close
      @closed="resetCreateForm"
    >
      <el-form
        ref="createFormRef"
        :model="createForm"
        :rules="createRules"
        :validate-on-rule-change="false"
        label-position="top"
        status-icon
        scroll-to-error
        @submit.prevent="handleCreate"
      >
        <el-form-item
          :label="$t('ticketCenter.problemType')"
          prop="type"
          required
        >
          <el-select
            v-model="createForm.type"
            :placeholder="$t('ticketCenter.problemTypePlaceholder')"
            class="full-width"
          >
            <el-option
              v-for="option in typeOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="问题截图">
          <div class="attachment-picker">
            <input
              ref="createFileInput"
              hidden
              type="file"
              accept="image/jpeg,image/png"
              multiple
              @change="selectCreateImages"
            />
            <el-button
              :disabled="createUploading || createAttachments.length >= 3"
              @click="createFileInput?.click()"
              >选择图片</el-button
            >
            <span>JPEG/PNG，单张不超过 5MB，最多 3 张</span>
          </div>
          <div v-if="createAttachments.length" class="attachment-list">
            <div
              v-for="item in createAttachments"
              :key="item.localId"
              class="attachment-item"
            >
              <img :src="item.preview" :alt="item.name" />
              <span>{{
                item.status === "uploading"
                  ? "上传中"
                  : item.status === "done"
                  ? "已上传"
                  : "上传失败"
              }}</span>
              <button type="button" @click.stop="removeCreateAttachment(item)">
                ×
              </button>
            </div>
          </div>
        </el-form-item>

        <div v-if="relatedTypeOptions.length" class="form-grid">
          <el-form-item
            :label="$t('ticketCenter.relatedType')"
            prop="relatedType"
            :required="requiresRelatedRecord"
          >
            <el-select
              v-model="createForm.relatedType"
              clearable
              :placeholder="
                requiresRelatedRecord
                  ? $t('ticketCenter.relatedTypeRequired')
                  : $t('common.optional')
              "
              class="full-width"
              @change="handleRelatedTypeChange"
            >
              <el-option
                v-for="option in relatedTypeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('ticketCenter.relatedId')"
            prop="relatedId"
            :required="requiresRelatedRecord"
          >
            <el-select
              v-model="createForm.relatedId"
              class="full-width"
              filterable
              :disabled="!createForm.relatedType"
              :loading="relatedOptionsLoading"
              :no-data-text="$t('ticketCenter.noRelatedRecords')"
              :placeholder="$t('ticketCenter.relatedRecordPlaceholder')"
            >
              <el-option
                v-for="option in relatedOptions"
                :key="`${option.relatedType}-${option.relatedId}`"
                :label="formatRelatedOption(option)"
                :value="String(option.relatedId)"
              />
            </el-select>
          </el-form-item>
        </div>
        <p v-if="requiresRelatedRecord" class="related-record-hint">
          {{ $t("ticketCenter.relatedRequiredHint") }}
        </p>

        <el-form-item :label="$t('ticketCenter.traceId')" prop="traceId">
          <el-input
            v-model.trim="createForm.traceId"
            maxlength="64"
            :placeholder="$t('ticketCenter.traceIdPlaceholder')"
          />
        </el-form-item>

        <el-form-item
          :label="$t('ticketCenter.description')"
          prop="content"
          required
        >
          <el-input
            v-model="createForm.content"
            type="textarea"
            :rows="7"
            minlength="5"
            maxlength="1000"
            show-word-limit
            resize="vertical"
            :placeholder="$t('ticketCenter.descriptionPlaceholder')"
          />
          <p class="field-hint">{{ $t("ticketCenter.descriptionHint") }}</p>
        </el-form-item>

        <div class="privacy-notice">
          <span aria-hidden="true">i</span>
          <p>{{ $t("ticketCenter.privacyNotice") }}</p>
        </div>
      </el-form>

      <template #footer>
        <el-button @click="createDialogVisible = false">{{
          $t("common.cancel")
        }}</el-button>
        <el-button
          type="primary"
          :loading="createLoading"
          :disabled="createLoading || createUploading"
          @click="handleCreate"
        >
          {{ $t("ticketCenter.submitTicket") }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  TicketStatus,
  TicketType,
  RelatedType,
  createTicket,
  getMyTickets,
  getMyTicketDetail,
  replyTicket,
  cancelTicket,
  closeTicket,
  markTicketRead,
  getTicketRelatedOptions,
  createTicketAttachmentUpload,
  uploadTicketImage,
} from "@/api/ticket";

export default {
  name: "TicketCenterView",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { t, locale } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const tickets = ref([]);
    const total = ref(0);
    const currentPage = ref(1);
    const pageSize = 12;
    const statusFilter = ref(null);
    const listLoading = ref(false);
    const detailLoading = ref(false);
    const selectedTicketId = ref(null);
    const detailTicket = ref(null);
    const messages = ref([]);
    const timelineRef = ref(null);
    const messageHasMore = ref(false);
    const messageNextCursor = ref(null);
    const olderMessagesLoading = ref(false);
    const replyContent = ref("");
    const replyLoading = ref(false);
    const replyFileInput = ref(null);
    const replyAttachments = ref([]);
    const replyUploading = computed(() =>
      replyAttachments.value.some((item) => item.status === "uploading")
    );
    const actionLoading = ref(false);
    const createDialogVisible = ref(false);
    const createLoading = ref(false);
    const createFileInput = ref(null);
    const createAttachments = ref([]);
    const createUploading = computed(() =>
      createAttachments.value.some((item) => item.status === "uploading")
    );
    const createFormRef = ref(null);
    const relatedOptionsLoading = ref(false);
    const relatedOptions = ref([]);
    const createForm = reactive({
      type: null,
      relatedType: null,
      relatedId: "",
      traceId: "",
      content: "",
    });

    const statusOptions = computed(() => [
      { value: null, label: t("ticketCenter.filters.all") },
      { value: TicketStatus.PENDING, label: statusLabel(TicketStatus.PENDING) },
      {
        value: TicketStatus.PROCESSING,
        label: statusLabel(TicketStatus.PROCESSING),
      },
      {
        value: TicketStatus.WAIT_USER,
        label: statusLabel(TicketStatus.WAIT_USER),
      },
      {
        value: TicketStatus.RESOLVED,
        label: statusLabel(TicketStatus.RESOLVED),
      },
      { value: TicketStatus.CLOSED, label: statusLabel(TicketStatus.CLOSED) },
      {
        value: TicketStatus.CANCELLED,
        label: statusLabel(TicketStatus.CANCELLED),
      },
    ]);

    const typeOptions = computed(() =>
      Object.values(TicketType)
        .filter((value) => value !== TicketType.PHONE_TASK)
        .map((value) => ({
          value,
          label: typeLabel(value),
        }))
    );

    const allowedRelatedTypes = {
      [TicketType.PHONE_TASK]: [RelatedType.SMS_TASK, RelatedType.SMS_PROJECT],
      [TicketType.SMS_CODE]: [RelatedType.SMS_FLOW, RelatedType.SMS_TASK],
      [TicketType.PROJECT]: [
        RelatedType.SMS_PROJECT,
        RelatedType.APPLY_PROJECT,
        RelatedType.APPLY_EXCLUSIVE,
      ],
      [TicketType.RECHARGE_WALLET]: [
        RelatedType.EASY_PAY_RECHARGE,
        RelatedType.USDT_RECHARGE,
        RelatedType.ID_CARD,
        RelatedType.WALLET_FLOW,
      ],
      [TicketType.DEV_WITHDRAW]: [RelatedType.DEV_WITHDRAW],
      [TicketType.DEVELOPER_API]: [RelatedType.SMS_FLOW],
      [TicketType.OTHER]: Object.values(RelatedType).filter(
        (value) => value !== RelatedType.NONE
      ),
    };

    const relatedTypeOptions = computed(() =>
      (allowedRelatedTypes[createForm.type] || []).map((value) => ({
        value,
        label: t(`ticketCenter.relatedTypes.type${value}`),
      }))
    );

    const requiresRelatedRecord = computed(() =>
      [TicketType.RECHARGE_WALLET, TicketType.DEV_WITHDRAW].includes(
        createForm.type
      )
    );

    const createRules = computed(() => ({
      type: [
        {
          validator: (_rule, value, callback) => {
            if (value) return callback();
            callback(new Error(t("ticketCenter.validationTypeRequired")));
          },
          trigger: "change",
        },
      ],
      relatedType: [
        {
          validator: (_rule, value, callback) => {
            if (!requiresRelatedRecord.value || value) return callback();
            callback(
              new Error(t("ticketCenter.validationRelatedTypeRequired"))
            );
          },
          trigger: "change",
        },
      ],
      relatedId: [
        {
          validator: (_rule, value, callback) => {
            const normalized = String(value || "").trim();
            if (!createForm.relatedType) return callback();
            if (!normalized && !completedIds(createAttachments.value).length) {
              callback(
                new Error(t("ticketCenter.validationRelatedIdRequired"))
              );
              return;
            }
            if (!/^[1-9]\d*$/.test(normalized)) {
              callback(new Error(t("ticketCenter.validationRelatedIdFormat")));
              return;
            }
            callback();
          },
          trigger: ["blur", "change"],
        },
      ],
      content: [
        {
          validator: (_rule, value, callback) => {
            const normalized = String(value || "").trim();
            if (!normalized) {
              callback(new Error(t("ticketCenter.validationContentRequired")));
              return;
            }
            const length = Array.from(normalized).length;
            if (length < 5 || length > 1000) {
              callback(new Error(t("ticketCenter.validationContentLength")));
              return;
            }
            callback();
          },
          trigger: "blur",
        },
      ],
    }));

    const canReply = computed(
      () =>
        detailTicket.value &&
        ![TicketStatus.CLOSED, TicketStatus.CANCELLED].includes(
          detailTicket.value.status
        )
    );

    const canCancel = computed(
      () =>
        detailTicket.value &&
        [TicketStatus.PENDING, TicketStatus.WAIT_USER].includes(
          detailTicket.value.status
        )
    );

    const canClose = computed(
      () => detailTicket.value?.status === TicketStatus.RESOLVED
    );

    function statusLabel(status) {
      return t(`ticketCenter.statuses.status${status}`);
    }

    function typeLabel(type) {
      return t(`ticketCenter.types.type${type}`);
    }

    function statusClass(status) {
      return `status-${status}`;
    }

    function senderClass(message) {
      if (message.recordType === 1 || message.senderType === 2)
        return "sender-system";
      return message.senderType === 1 ? "sender-admin" : "sender-user";
    }

    function senderLabel(message) {
      if (message.recordType === 1 || message.senderType === 2)
        return t("ticketCenter.sender.system");
      if (message.senderType === 1)
        return message.senderName || t("ticketCenter.sender.support");
      return t("ticketCenter.sender.you");
    }

    function senderInitial(message) {
      if (message.recordType === 1 || message.senderType === 2) return "•";
      return message.senderType === 1 ? "S" : "U";
    }

    function formatTime(value) {
      if (!value) return "--";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat(
        locale.value.startsWith("zh") ? "zh-CN" : "en-US",
        {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }
      ).format(date);
    }

    function formatRelatedOption(option) {
      const parts = [t(`ticketCenter.relatedTypes.type${option.relatedType}`)];
      parts.push(option.referenceNo || `#${option.relatedId}`);
      if (option.amount)
        parts.push(`${t("ticketCenter.amount")} ${option.amount}`);
      if (option.createTime) parts.push(formatTime(option.createTime));
      return parts.join(" · ");
    }

    async function fetchTickets() {
      listLoading.value = true;
      try {
        const params = { page: currentPage.value, pageSize };
        if (statusFilter.value !== null) params.status = statusFilter.value;
        const response = await getMyTickets(params);
        tickets.value = response.data?.list || [];
        total.value = response.data?.total || 0;

        if (!selectedTicketId.value && tickets.value.length) {
          await openTicket(tickets.value[0]);
        } else if (selectedTicketId.value) {
          const stillVisible = tickets.value.some(
            (ticket) => ticket.id === selectedTicketId.value
          );
          if (!stillVisible) {
            selectedTicketId.value = null;
            detailTicket.value = null;
            messages.value = [];
          }
        }
      } catch (error) {
        console.error("Fetch tickets failed:", error);
      } finally {
        listLoading.value = false;
      }
    }

    async function openTicket(ticketOrId) {
      const id = typeof ticketOrId === "object" ? ticketOrId.id : ticketOrId;
      if (!id) return;
      selectedTicketId.value = id;
      detailLoading.value = true;
      try {
        const response = await getMyTicketDetail(id);
        detailTicket.value = response.data?.ticket || null;
        messages.value = response.data?.messages || [];
        messageHasMore.value = Boolean(response.data?.messageHasMore);
        messageNextCursor.value = response.data?.messageNextCursor || null;
        replyContent.value = "";
        if (detailTicket.value?.userUnread) {
          try {
            await markTicketRead(id);
            detailTicket.value.userUnread = false;
            const listTicket = tickets.value.find((ticket) => ticket.id === id);
            if (listTicket) listTicket.userUnread = false;
          } catch (error) {
            console.error("Mark ticket read failed:", error);
          }
        }
      } catch (error) {
        console.error("Fetch ticket detail failed:", error);
        selectedTicketId.value = null;
        messageHasMore.value = false;
        messageNextCursor.value = null;
      } finally {
        detailLoading.value = false;
      }
    }

    async function loadOlderMessages() {
      if (
        !selectedTicketId.value ||
        !messageHasMore.value ||
        !messageNextCursor.value ||
        olderMessagesLoading.value
      )
        return;
      const currentTicketId = selectedTicketId.value;
      const container = timelineRef.value;
      const previousHeight = container?.scrollHeight || 0;
      olderMessagesLoading.value = true;
      try {
        const response = await getMyTicketDetail(
          currentTicketId,
          messageNextCursor.value
        );
        if (currentTicketId !== selectedTicketId.value) return;
        const older = Array.isArray(response.data?.messages)
          ? response.data.messages
          : [];
        const existingIds = new Set(
          messages.value.map((message) => message.id)
        );
        messages.value = [
          ...older.filter((message) => !existingIds.has(message.id)),
          ...messages.value,
        ];
        messageHasMore.value = Boolean(response.data?.messageHasMore);
        messageNextCursor.value = response.data?.messageNextCursor || null;
        await nextTick();
        if (container)
          container.scrollTop += container.scrollHeight - previousHeight;
      } catch (error) {
        console.error("Fetch older ticket messages failed:", error);
      } finally {
        olderMessagesLoading.value = false;
      }
    }

    function changeStatusFilter(status) {
      statusFilter.value = status;
      currentPage.value = 1;
      selectedTicketId.value = null;
      detailTicket.value = null;
      messages.value = [];
      fetchTickets();
    }

    function openCreateDialog() {
      createDialogVisible.value = true;
    }

    function resetCreateForm() {
      createForm.type = null;
      createForm.relatedType = null;
      createForm.relatedId = "";
      createForm.traceId = "";
      createForm.content = "";
      clearAttachments(createAttachments.value);
      createAttachments.value = [];
      relatedOptions.value = [];
      nextTick(() => createFormRef.value?.clearValidate());
    }

    async function loadRelatedOptions(relatedType) {
      relatedOptions.value = [];
      if (!relatedType) return;
      relatedOptionsLoading.value = true;
      try {
        const response = await getTicketRelatedOptions(relatedType);
        relatedOptions.value = Array.isArray(response.data)
          ? response.data
          : [];
      } catch (error) {
        console.error("Fetch ticket related options failed:", error);
      } finally {
        relatedOptionsLoading.value = false;
      }
    }

    function handleRelatedTypeChange(relatedType) {
      createForm.relatedId = "";
      loadRelatedOptions(relatedType);
      nextTick(() => createFormRef.value?.clearValidate("relatedId"));
    }

    async function handleCreate() {
      if (createLoading.value || !createFormRef.value) return;
      try {
        await createFormRef.value.validate();
      } catch {
        ElMessage.warning(t("ticketCenter.validationIncomplete"));
        return;
      }
      createLoading.value = true;
      try {
        const payload = {
          type: createForm.type,
          content: createForm.content.trim(),
          relatedType: createForm.relatedType || RelatedType.NONE,
        };
        payload.attachmentIds = completedIds(createAttachments.value);
        if (createForm.relatedId.trim())
          payload.relatedId = createForm.relatedId.trim();
        if (createForm.traceId.trim())
          payload.traceId = createForm.traceId.trim();
        const response = await createTicket(payload);
        const createdId = response.data?.ticketId || response.data?.id;
        ElMessage.success(
          t("ticketCenter.createSuccess", {
            ticketNo: response.data?.ticketNo || "",
          })
        );
        createDialogVisible.value = false;
        statusFilter.value = null;
        currentPage.value = 1;
        selectedTicketId.value = null;
        await fetchTickets();
        if (createdId) await openTicket(createdId);
        await router.replace({
          path: "/tickets",
          query: createdId ? { id: createdId } : {},
        });
      } catch (error) {
        console.error("Create ticket failed:", error);
      } finally {
        createLoading.value = false;
      }
    }

    async function handleReply() {
      const content = replyContent.value.trim();
      const attachmentIds = completedIds(replyAttachments.value);
      if (
        (!content && !attachmentIds.length) ||
        !detailTicket.value ||
        replyUploading.value
      )
        return;
      replyLoading.value = true;
      try {
        await replyTicket({
          ticketId: detailTicket.value.id,
          content,
          attachmentIds,
        });
        ElMessage.success(t("ticketCenter.replySuccess"));
        replyContent.value = "";
        clearAttachments(replyAttachments.value);
        replyAttachments.value = [];
        await openTicket(detailTicket.value.id);
        await fetchTickets();
      } catch (error) {
        console.error("Reply ticket failed:", error);
      } finally {
        replyLoading.value = false;
      }
    }

    function completedIds(items) {
      return items
        .filter((item) => item.status === "done")
        .map((item) => item.id);
    }
    function clearAttachments(items) {
      items.forEach((item) => {
        item.controller?.abort();
        URL.revokeObjectURL(item.preview);
      });
    }
    function removeAttachment(target, item) {
      item.controller?.abort();
      URL.revokeObjectURL(item.preview);
      const index = target.indexOf(item);
      if (index >= 0) target.splice(index, 1);
    }

    function removeReplyAttachment(item) {
      removeAttachment(replyAttachments.value, item);
    }

    function removeCreateAttachment(item) {
      removeAttachment(createAttachments.value, item);
    }
    async function addImages(files, target, ticketId) {
      const selected = Array.from(files || []).slice(
        0,
        Math.max(0, 3 - target.length)
      );
      for (const file of selected) {
        if (
          !["image/jpeg", "image/png"].includes(file.type) ||
          file.size > 5 * 1024 * 1024
        ) {
          ElMessage.warning("仅支持 5MB 以内的 JPEG/PNG 图片");
          continue;
        }
        const item = reactive({
          localId: `${Date.now()}-${Math.random()}`,
          name: file.name,
          preview: URL.createObjectURL(file),
          status: "uploading",
          id: null,
          controller: new AbortController(),
        });
        target.push(item);
        try {
          const response = await createTicketAttachmentUpload({
            ticketId: ticketId || null,
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
          });
          await uploadTicketImage(response.data, file, item.controller.signal);
          item.id = response.data.attachmentId;
          item.status = "done";
        } catch (error) {
          if (error?.name === "AbortError") {
            continue;
          }
          item.status = "error";
          ElMessage.error("图片上传失败，请删除后重试");
        }
      }
    }
    function selectCreateImages(event) {
      addImages(event.target.files, createAttachments.value, null);
      event.target.value = "";
    }
    function selectReplyImages(event) {
      addImages(
        event.target.files,
        replyAttachments.value,
        detailTicket.value?.id
      );
      event.target.value = "";
    }

    async function handleCancel() {
      if (!detailTicket.value) return;
      try {
        await ElMessageBox.confirm(
          t("ticketCenter.cancelConfirm"),
          t("ticketCenter.cancelTicket"),
          {
            type: "warning",
            confirmButtonText: t("common.confirm"),
            cancelButtonText: t("common.cancel"),
          }
        );
        actionLoading.value = true;
        await cancelTicket({
          ticketId: detailTicket.value.id,
          reason: t("ticketCenter.userCancelledReason"),
        });
        ElMessage.success(t("ticketCenter.cancelSuccess"));
        await openTicket(detailTicket.value.id);
        await fetchTickets();
      } catch (error) {
        if (error !== "cancel" && error !== "close")
          console.error("Cancel ticket failed:", error);
      } finally {
        actionLoading.value = false;
      }
    }

    async function handleClose() {
      if (!detailTicket.value) return;
      try {
        await ElMessageBox.confirm(
          t("ticketCenter.closeConfirm"),
          t("ticketCenter.confirmClose"),
          {
            type: "success",
            confirmButtonText: t("common.confirm"),
            cancelButtonText: t("common.cancel"),
          }
        );
        actionLoading.value = true;
        await closeTicket({ ticketId: detailTicket.value.id });
        ElMessage.success(t("ticketCenter.closeSuccess"));
        await openTicket(detailTicket.value.id);
        await fetchTickets();
      } catch (error) {
        if (error !== "cancel" && error !== "close")
          console.error("Close ticket failed:", error);
      } finally {
        actionLoading.value = false;
      }
    }

    watch(
      () => route.query.create,
      (value) => {
        if (value === "true") openCreateDialog();
      }
    );

    watch(
      () => createForm.type,
      () => {
        createForm.relatedType = null;
        createForm.relatedId = "";
        relatedOptions.value = [];
        nextTick(() =>
          createFormRef.value?.clearValidate(["relatedType", "relatedId"])
        );
        if (
          requiresRelatedRecord.value &&
          relatedTypeOptions.value.length === 1
        ) {
          createForm.relatedType = relatedTypeOptions.value[0].value;
          loadRelatedOptions(createForm.relatedType);
        }
      }
    );

    onMounted(async () => {
      await fetchTickets();
      if (route.query.id) await openTicket(Number(route.query.id));
      if (route.query.create === "true") openCreateDialog();
    });

    return {
      TicketStatus,
      tickets,
      total,
      currentPage,
      pageSize,
      statusFilter,
      statusOptions,
      typeOptions,
      relatedTypeOptions,
      requiresRelatedRecord,
      listLoading,
      detailLoading,
      selectedTicketId,
      detailTicket,
      messages,
      timelineRef,
      messageHasMore,
      olderMessagesLoading,
      replyContent,
      replyFileInput,
      replyAttachments,
      replyUploading,
      replyLoading,
      actionLoading,
      createDialogVisible,
      createLoading,
      createFileInput,
      createAttachments,
      createUploading,
      relatedOptionsLoading,
      relatedOptions,
      createFormRef,
      createForm,
      createRules,
      canReply,
      canCancel,
      canClose,
      statusLabel,
      typeLabel,
      statusClass,
      senderClass,
      senderLabel,
      senderInitial,
      formatTime,
      formatRelatedOption,
      fetchTickets,
      openTicket,
      loadOlderMessages,
      changeStatusFilter,
      openCreateDialog,
      resetCreateForm,
      handleRelatedTypeChange,
      handleCreate,
      handleReply,
      handleCancel,
      handleClose,
      selectCreateImages,
      selectReplyImages,
      removeAttachment,
      removeReplyAttachment,
      removeCreateAttachment,
      completedIds,
    };
  },
};
</script>

<style scoped>
.ticket-center-page {
  min-height: calc(100vh - var(--header-height) - var(--footer-height));
  padding: 48px 0 72px;
  background: radial-gradient(
      circle at 8% 5%,
      rgba(68, 97, 242, 0.1),
      transparent 28%
    ),
    radial-gradient(
      circle at 92% 12%,
      rgba(56, 191, 255, 0.12),
      transparent 24%
    ),
    var(--background-color);
}

.ticket-center-page.embedded {
  min-height: auto;
  padding-top: 24px;
  background: transparent;
}

.embedded-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.ticket-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 28px;
}

.section-kicker {
  display: inline-block;
  margin-bottom: 8px;
  color: var(--primary-color);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.ticket-hero h1 {
  margin: 0 0 10px;
  color: var(--text-primary);
  font-size: clamp(30px, 4vw, 44px);
  line-height: 1.12;
}

.ticket-hero p {
  max-width: 680px;
  color: var(--text-secondary);
  font-size: 16px;
}

.button-symbol {
  margin-right: 6px;
  font-size: 20px;
  line-height: 1;
}

.ticket-shell {
  display: grid;
  grid-template-columns: minmax(320px, 380px) minmax(0, 1fr);
  height: calc(100dvh - var(--header-height) - 48px);
  min-height: 680px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: var(--shadow-lg);
}

.ticket-list-card {
  display: flex;
  min-height: 0;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid var(--border-color);
  background: rgba(248, 250, 252, 0.8);
}

.list-toolbar {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 18px;
  border-bottom: 1px solid var(--border-color);
}

.status-filter {
  display: flex;
  min-width: 0;
  flex: 1;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
}

.status-filter::-webkit-scrollbar {
  display: none;
}

.filter-chip,
.refresh-button,
.ticket-list-item {
  font: inherit;
}

.filter-chip {
  flex: 0 0 auto;
  padding: 7px 12px;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: #fff;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 13px;
  transition: 0.2s ease;
}

.filter-chip:hover,
.filter-chip.active {
  border-color: var(--primary-color);
  background: rgba(68, 97, 242, 0.08);
  color: var(--primary-color);
}

.refresh-button {
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: #fff;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 19px;
}

.refresh-button:disabled {
  cursor: wait;
  opacity: 0.5;
}

.list-loading,
.empty-list {
  padding: 24px;
}

.list-loading :deep(.el-skeleton) {
  margin-bottom: 24px;
}

.ticket-list {
  flex: 1;
  overflow-y: auto;
}

.ticket-list-item {
  position: relative;
  display: block;
  width: 100%;
  padding: 18px 20px;
  border: 0;
  border-bottom: 1px solid var(--border-color);
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: 0.2s ease;
}

.ticket-list-item::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: var(--primary-color);
  content: "";
  opacity: 0;
}

.ticket-list-item:hover,
.ticket-list-item.selected {
  background: #fff;
}

.ticket-list-item.selected::before {
  opacity: 1;
}

.ticket-item-topline,
.detail-eyebrow,
.detail-meta,
.timeline-meta,
.reply-actions {
  display: flex;
  align-items: center;
}

.ticket-item-topline {
  gap: 8px;
}

.ticket-number {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: var(--text-secondary);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unread-dot {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

.status-pill {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-0 {
  background: #fff7ed;
  color: #c2410c;
}
.status-1 {
  background: #eff6ff;
  color: #1d4ed8;
}
.status-2 {
  background: #fff1f2;
  color: #be123c;
}
.status-3 {
  background: #ecfdf5;
  color: #047857;
}
.status-4,
.status-5 {
  background: #f1f5f9;
  color: #64748b;
}

.ticket-item-type {
  margin-top: 12px;
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 700;
}

.ticket-summary {
  display: -webkit-box;
  margin: 6px 0 12px;
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 13px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.ticket-list-item time {
  color: var(--text-tertiary);
  font-size: 12px;
}

.empty-list {
  margin: auto 0;
  color: var(--text-secondary);
  text-align: center;
}

.empty-icon {
  margin-bottom: 12px;
  color: var(--primary-color);
  font-size: 42px;
}

.empty-list h3 {
  margin-bottom: 6px;
  color: var(--text-primary);
}

.empty-list p {
  margin-bottom: 18px;
  font-size: 14px;
}

.ticket-list-card :deep(.el-pagination) {
  justify-content: center;
  padding: 14px;
  border-top: 1px solid var(--border-color);
}

.ticket-detail-card {
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: #fff;
}

.detail-loading,
.detail-content,
.detail-placeholder {
  padding: 32px clamp(24px, 4vw, 48px);
}

.detail-loading,
.detail-content {
  height: 100%;
}

.detail-content {
  display: flex;
  min-height: 0;
  flex-direction: column;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
}

.detail-eyebrow {
  flex-wrap: wrap;
  gap: 10px;
  color: var(--text-secondary);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
}

.detail-header h2 {
  margin: 12px 0 8px;
  color: var(--text-primary);
  font-size: 26px;
}

.detail-meta {
  flex-wrap: wrap;
  gap: 8px 18px;
  color: var(--text-tertiary);
  font-size: 13px;
}

.detail-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.attention-banner,
.resolved-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
}

.attention-banner {
  background: #fff7ed;
  color: #9a3412;
}

.resolved-banner {
  background: #ecfdf5;
  color: #065f46;
}

.attention-banner span,
.resolved-banner span,
.privacy-notice > span {
  display: inline-grid;
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid currentColor;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 800;
}

.timeline {
  position: relative;
  display: flex;
  min-height: 220px;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 20px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 30px 12px 30px 0;
  scrollbar-color: var(--border-color) transparent;
  scrollbar-width: thin;
}

.timeline::-webkit-scrollbar {
  width: 6px;
}

.timeline::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: var(--border-color);
}

.timeline::before {
  position: absolute;
  top: 30px;
  bottom: 30px;
  left: 18px;
  width: 1px;
  background: var(--border-color);
  content: "";
}

.timeline-load-older {
  z-index: 1;
  display: flex;
  justify-content: center;
}

.timeline-item {
  position: relative;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 14px;
}

.timeline-avatar {
  z-index: 1;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 800;
}

.sender-admin .timeline-avatar {
  background: #dbeafe;
  color: #1d4ed8;
}

.sender-system .timeline-avatar {
  background: #f1f5f9;
  color: #64748b;
}

.timeline-body {
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid var(--border-color);
  border-radius: 4px 14px 14px;
  background: #fff;
}

.sender-admin .timeline-body {
  border-color: #bfdbfe;
  background: #f8fbff;
}

.sender-system .timeline-body {
  background: #f8fafc;
}

.timeline-meta {
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.timeline-meta strong {
  color: var(--text-primary);
  font-size: 13px;
}

.timeline-meta time {
  color: var(--text-tertiary);
  font-size: 12px;
}

.timeline-body p {
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.7;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.ticket-image {
  width: 92px;
  height: 92px;
  margin: 10px 8px 0 0;
  border-radius: 6px;
}
.attachment-picker {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  color: var(--text-tertiary);
  font-size: 12px;
}
.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}
.attachment-item {
  position: relative;
  width: 88px;
}
.attachment-item img {
  width: 88px;
  height: 72px;
  border-radius: 6px;
  object-fit: cover;
}
.attachment-item span {
  display: block;
  overflow: hidden;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attachment-item button {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  cursor: pointer;
}

.reply-panel,
.terminal-panel {
  flex: 0 0 auto;
  padding: 20px;
  border: 1px solid var(--border-color);
  border-radius: 14px;
  background: #f8fafc;
}

.reply-panel > label {
  display: block;
  margin-bottom: 10px;
  color: var(--text-primary);
  font-weight: 700;
}

.reply-actions {
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
}

.reply-actions > span {
  color: var(--text-tertiary);
  font-size: 12px;
}

.terminal-panel {
  text-align: center;
}

.terminal-panel p {
  margin: 8px 0 16px;
  color: var(--text-secondary);
}

.detail-placeholder {
  display: flex;
  min-height: 680px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--text-secondary);
  text-align: center;
}

.placeholder-illustration {
  display: flex;
  width: 110px;
  height: 80px;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
  margin-bottom: 22px;
  border-radius: 20px;
  background: linear-gradient(145deg, #eef2ff, #f0f9ff);
}

.placeholder-illustration span {
  width: 12px;
  margin-bottom: 24px;
  border-radius: 999px;
  background: var(--primary-color);
}

.placeholder-illustration span:nth-child(1) {
  height: 18px;
  opacity: 0.45;
}
.placeholder-illustration span:nth-child(2) {
  height: 30px;
  opacity: 0.75;
}
.placeholder-illustration span:nth-child(3) {
  height: 22px;
}

.detail-placeholder h2 {
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 22px;
}

.full-width {
  width: 100%;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.privacy-notice {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 13px;
}

.related-record-hint {
  margin: -8px 0 18px;
  color: #b45309;
  font-size: 13px;
}

.field-hint {
  margin-top: 6px;
  color: var(--text-tertiary);
  font-size: 12px;
  line-height: 1.5;
}

.privacy-notice p {
  margin: 0;
  line-height: 1.55;
}

@media (max-width: 900px) {
  .ticket-shell {
    height: auto;
    min-height: 0;
    grid-template-columns: 1fr;
  }

  .ticket-list-card {
    max-height: 520px;
    border-right: 0;
    border-bottom: 1px solid var(--border-color);
  }

  .ticket-detail-card {
    height: calc(100dvh - 24px);
    min-height: 600px;
  }

  .detail-placeholder {
    min-height: 360px;
  }
}

@media (max-width: 640px) {
  .ticket-center-page {
    padding: 28px 0 48px;
  }

  .ticket-hero {
    align-items: stretch;
    flex-direction: column;
    gap: 18px;
  }

  .ticket-hero :deep(.el-button) {
    width: 100%;
  }

  .ticket-shell {
    border-radius: 14px;
  }

  .ticket-detail-card {
    height: calc(100dvh - 16px);
    min-height: 560px;
  }

  .detail-content,
  .detail-loading {
    padding: 24px 18px;
  }

  .detail-header {
    flex-direction: column;
  }

  .detail-actions {
    width: 100%;
  }

  .detail-actions :deep(.el-button) {
    flex: 1;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .reply-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .reply-actions :deep(.el-button) {
    width: 100%;
  }
}
</style>
